import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import { styled } from "@mui/material/styles";
import TableRowComponent from "components/table/TableRowComponent";
import TableCellComponent from "components/table/TableCellComponent";
import SearchBox from "components/SearchBox";
import useLoadMore from "hooks/useLoadMore";
import insertObjectIf from "util/helper";
import { Item, ItemSelectRequest, ItemUpdateRequest } from "../types/item";
import useItemList from "../hooks/useItemList";
import ItemFormModal from "../components/ItemFormModal";
import { updateItem } from "../api/itemApi";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: "var(--offwhite-300)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--white)",
  },
}));

/**
 * The ItemList Page.
 */
function ItemList() {
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useState<Omit<ItemSelectRequest, "limit" | "offset">>({
    item_cd: "",
    brand_cd: "",
    jan_cd: "",
    item_category_l: "",
    item_category_m: "",
    item_category_s: "",
    item_name: "",
    main_vendor_cd: "",
    ec_flag: 0,
    status_flag: 1,
  });
  const { items, count, fetchData, onLoadMore, loading } = useItemList(searchParams, page, setPage);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { renderNodeLoader } = useLoadMore(onLoadMore, loading);

  const navigate = useNavigate();

  const handleSettingPrice = (item: Item) => {
    navigate(`/mst-item/${item.item_cd}/edit-price`);
  };

  const handleSearchClick = async () => {
    setPage(1);
    if (page === 1) {
      await fetchData();
    }
  };

  const handleDetailClick = (item: Item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const handleSearchCheckbox = (
    event: React.ChangeEvent<HTMLInputElement>,
    key: "ec_flag" | "status_flag",
  ) => {
    const value = event.target.checked ? 1 : 0;
    setSearchParams((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmitUpdateItem = async (item: Item) => {
    const isNumericSet = (v: unknown): v is number => v !== "" && v !== undefined && v !== null;
    const itemUpdateRequest: ItemUpdateRequest = {
      item_cd: item.item_cd,
      mst_item_detail: {
        ...insertObjectIf(isNumericSet(item.status_flag), { status_flag: item.status_flag }),
        ...insertObjectIf(isNumericSet(item.ec_flag), { ec_flag: item.ec_flag }),
        vendor_cd_2nd: item.vendor_cd_2nd ?? "",
        vendor_cd_3rd: item.vendor_cd_3rd ?? "",
        hatchu_unit: item.hatchu_unit ?? 0,
      },
    };

    try {
      await updateItem(itemUpdateRequest);
      await fetchData();
      handleCloseModal();
    } catch (error) {
      console.error("Error updating item:", error);
    }
  };

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box sx={{ p: { xs: 2, md: 2.5, lg: 5 } }}>
        <Box display="flex" sx={{ gap: 5, flexWrap: { xs: "wrap", md: "nowrap" } }}>
          <Box sx={{ textAlign: "right", display: "inline-block" }}>
            <Typography
              margin={0}
              textAlign="right"
              fontSize={{ xs: 24, lg: 33 }}
              whiteSpace="nowrap"
              color="var(--gray-700)"
            >
              マスタ管理
            </Typography>
            <Typography
              margin={0}
              textAlign="right"
              fontSize={{ xs: 20, lg: 28 }}
              color="var(--gray-700)"
            >
              商品一覧
            </Typography>
          </Box>
          <Box display="flex" columnGap="15px" rowGap="4px" margin="auto" flexWrap="wrap">
            <SearchBox
              title="YH商品コード"
              id="code-name"
              placeholder=""
              type="number"
              sx={{
                width: {
                  xs: "100%",
                  md: "calc((100% - 15px) / 2)",
                  xl: "calc((100% - 30px) / 3)",
                },
              }}
              value={searchParams.item_cd}
              onChangeText={(value) => setSearchParams((prev) => ({ ...prev, item_cd: value }))}
            />
            <SearchBox
              title="JANコード"
              id="brand-name"
              placeholder=""
              type="number"
              sx={{
                width: {
                  xs: "100%",
                  md: "calc((100% - 15px) / 2)",
                  xl: "calc((100% - 30px) / 3)",
                },
              }}
              value={searchParams.jan_cd}
              onChangeText={(value) => setSearchParams((prev) => ({ ...prev, jan_cd: value }))}
            />
            <SearchBox
              title="ブランドコード"
              id="category-large"
              placeholder=""
              type="number"
              sx={{
                width: {
                  xs: "100%",
                  md: "calc((100% - 15px) / 2)",
                  xl: "calc((100% - 30px) / 3)",
                },
              }}
              value={searchParams.brand_cd}
              onChangeText={(value) => setSearchParams((prev) => ({ ...prev, brand_cd: value }))}
            />
            <SearchBox
              title="大分類"
              id="category-large"
              placeholder=""
              sx={{
                width: {
                  xs: "100%",
                  md: "calc((100% - 15px) / 2)",
                  xl: "calc((100% - 30px) / 3)",
                },
              }}
              value={searchParams.item_category_l}
              onChangeText={(value) =>
                setSearchParams((prev) => ({ ...prev, item_category_l: value }))
              }
            />
            <SearchBox
              title="中分類"
              id="category-large"
              placeholder=""
              sx={{
                width: {
                  xs: "100%",
                  md: "calc((100% - 15px) / 2)",
                  xl: "calc((100% - 30px) / 3)",
                },
              }}
              value={searchParams.item_category_m}
              onChangeText={(value) =>
                setSearchParams((prev) => ({ ...prev, item_category_m: value }))
              }
            />
            <SearchBox
              title="小分類"
              id="category-large"
              placeholder=""
              sx={{
                width: {
                  xs: "100%",
                  md: "calc((100% - 15px) / 2)",
                  xl: "calc((100% - 30px) / 3)",
                },
              }}
              value={searchParams.item_category_s}
              onChangeText={(value) =>
                setSearchParams((prev) => ({ ...prev, item_category_s: value }))
              }
            />
            <SearchBox
              title="商品名"
              id="category-large"
              placeholder=""
              sx={{
                flexGrow: { md: 0, xl: 1 },
                width: {
                  xs: "100%",
                  md: "calc((100% - 15px) / 2)",
                },
              }}
              value={searchParams.item_name}
              onChangeText={(value) => setSearchParams((prev) => ({ ...prev, item_name: value }))}
            />
            <SearchBox
              title="仕入先"
              id="category-large"
              placeholder=""
              sx={{
                width: {
                  xs: "100%",
                  md: "calc((100% - 15px) / 2)",
                  xl: "calc((100% - 30px) / 3)",
                },
              }}
              value={searchParams.main_vendor_cd}
              onChangeText={(value) =>
                setSearchParams((prev) => ({ ...prev, main_vendor_cd: value }))
              }
            />
          </Box>
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          marginTop="25px"
          marginBottom="25px"
          width="100%"
        >
          <Box display="flex" alignItems="center" gap={2} justifyContent="flex-start">
            <FormControlLabel
              control={
                <Checkbox
                  checked={searchParams.ec_flag === 1}
                  onChange={(event) => handleSearchCheckbox(event, "ec_flag")}
                  color="secondary"
                />
              }
              label={
                <Typography sx={{ fontSize: { xs: 16, md: 18 }, color: "var(--black)" }}>
                  ECフラグ付
                </Typography>
              }
              sx={{ whiteSpace: "pre" }}
            />
            {/* <FormControlLabel
              control={
                <Checkbox
                  checked={searchParams.status_flag === 1}
                  onChange={(event) => handleSearchCheckbox(event, "status_flag")}
                  color="secondary"
                />
              }
              label={
                <Typography sx={{ fontSize: { xs: 16, md: 18 }, color: "var(--black)" }}>
                  有効のみ
                </Typography>
              }
              sx={{ whiteSpace: "pre" }}
            /> */}
          </Box>
          <Box display="flex" justifyContent="center" alignItems="center" ml={2}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "var(--gray-500)",
                color: "var(--white)",
                width: "190px",
                height: "50px",
                fontSize: { xs: 16, md: 18, lg: 25 },
                borderRadius: "9px",
                ":hover": {
                  backgroundColor: "var(--gray-250)",
                  boxShadow: "var(--shadow)",
                },
              }}
              onClick={handleSearchClick}
            >
              検索
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{ p: { xs: 2, md: 2.5, lg: 5 }, backgroundColor: "var(--offwhite-100)" }}
      >
        <Box
          display="flex"
          justifyContent="flex-end"
          marginBottom="25px"
          alignItems="center"
          columnGap="9px"
          sx={{ width: { xs: "100%", lg: "90%" } }}
        >
          <Box fontSize={{ xs: 16, md: 18, lg: 20 }} marginLeft="55px">
            件数【{count}件】
          </Box>
        </Box>
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Table sx={{ minWidth: { xs: 1300, lg: 1500 }, boxShadow: "none" }}>
            <TableHead>
              <TableRowComponent
                data={[
                  { label: "YH商品コード", sx: { px: 1 } },
                  { label: "ブランド名", sx: { px: 1 } },
                  { label: "大分類", sx: { px: 1 } },
                  { label: "中分類", sx: { px: 1 } },
                  { label: "小分類", sx: { px: 1 } },
                  { label: "商品名", sx: { px: 1 } },
                  { label: "仕入先", sx: { px: 1 } },
                  { label: "仕入原価", sx: { px: 1 } },
                  { label: "定価（税抜）", sx: { px: 1 } },
                  // { label: "価格", sx: { px: 1 } },
                  { label: "詳細", sx: { px: 1 } },
                ]}
                type="header"
              />
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <StyledTableRow
                  key={Math.floor(Math.random() * 100) + item.item_cd}
                  className={index % 2 === 0 ? "even" : "odd"}
                >
                  <TableCellComponent>{item.item_cd}</TableCellComponent>
                  <TableCellComponent>{item.brand_name}</TableCellComponent>
                  <TableCellComponent>{item.item_category_l}</TableCellComponent>
                  <TableCellComponent>{item.item_category_m}</TableCellComponent>
                  <TableCellComponent>{item.item_category_s}</TableCellComponent>
                  <TableCellComponent sx={{ textAlign: "left" }}>
                    {item.item_name}
                  </TableCellComponent>
                  <TableCellComponent>{item.main_vendor_cd}</TableCellComponent>
                  <TableCellComponent>
                    {item.main_purchase_cost.toLocaleString()}
                  </TableCellComponent>
                  <TableCellComponent>{item.list_price.toLocaleString()}</TableCellComponent>
                  {/* <TableCellComponent sx={{ px: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => handleSettingPrice(item)}
                      sx={{
                        border: "1px solid var(--gray-250)",
                        backgroundColor: "var(--white)",
                        color: "var(--black)",
                        fontSize: { xs: 16, md: 18, lg: 20 },
                        borderRadius: "4px",
                        width: { xs: "100%", md: 120, lg: 130 },
                        height: "40px",
                        whiteSpace: "pre",
                        boxShadow: "var(--shadow)",
                      }}
                    >
                      価格
                    </Button>
                  </TableCellComponent> */}
                  <TableCellComponent sx={{ px: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => handleDetailClick(item)}
                      sx={{
                        border: "1px solid var(--gray-250)",
                        backgroundColor: "var(--white)",
                        color: "var(--black)",
                        fontSize: { xs: 16, md: 18, lg: 20 },
                        borderRadius: "4px",
                        width: { xs: "100%", md: 120, lg: 130 },
                        height: "40px",
                        whiteSpace: "pre",
                        boxShadow: "var(--shadow)",
                      }}
                    >
                      詳細
                    </Button>
                  </TableCellComponent>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
          {renderNodeLoader()}
        </Box>
      </Box>
      {selectedItem && (
        <ItemFormModal
          itemDetail={selectedItem}
          onClose={handleCloseModal}
          visible={isModalOpen}
          isLoading={loading}
          onHandleSubmit={handleSubmitUpdateItem}
        />
      )}
    </>
  );
}

export default ItemList;
